import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Portal,
  Select,
  Spinner,
  Switch,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  Tooltip,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { AddIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, DeleteIcon, EditIcon, WarningIcon } from "@chakra-ui/icons";
import { FilterProto, Header, MetricProto, ObservationColumnProto } from "build-ts/metadata";
import React, { useEffect, useMemo, useState } from "react";
import { getMetricById, listMetrics } from "services/MetricService";
import showToast from "hooks/useCustomToast";
import { ColumnType, FilterOperator, MetadataTypeEnum, MetricEdgeType } from "build-ts/enums";
import { KGObservationProto } from "build-ts/analysis_plan";
import { getKGObservationData } from "services/AnalysisTreeServices";
import ColumnValues from "components/common/ColumnValues";
import { AnalystProto, ColumnScopeProto } from "build-ts/analyst";
import { KGColumnProto } from "build-ts/knowledege_graph";
import { IoMdContract, IoMdInformationCircleOutline } from "react-icons/io";
import { getTooltip } from "utils/getMessages";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { refreshAnalystDataScope } from "services/AnalystService";
import { FaChartPie, FaDatabase, FaExpandAlt, FaSync, FaUser } from "react-icons/fa";
import { FiCalendar } from "react-icons/fi";
import { useUser } from "context/UserContext";
import { MdBarChart, MdNumbers } from "react-icons/md";
import { PiTarget } from "react-icons/pi";
import { BsThreeDotsVertical } from "react-icons/bs";

interface DataScopeProps {
  originalAnalyst?: AnalystProto;
  analystName: string;
  setAnalystName: (value: string) => void;
  analystDescription: string;
  setAnalystDescription: (value: string) => void;
  instructions: string;
  setInstructions: (value: string) => void;
  useDefaultScope: boolean;
  setUseDefaultScope: (value: boolean) => void;
  selectedMetricsWithEdges: MetricProto[];
  setSelectedMetricsWithEdges: React.Dispatch<React.SetStateAction<MetricProto[]>>;
  selectedBenchmarks: Header[];
  setSelectedBenchmarks: React.Dispatch<React.SetStateAction<Header[]>>;
  columnScopes: ColumnScopeProto[];
  setColumnScopes: React.Dispatch<React.SetStateAction<ColumnScopeProto[]>>;
  isCreatingNew?: boolean;
  isLoadingDataScope?: boolean;
}

const DataScopeConfigurator: React.FC<DataScopeProps> = ({
  originalAnalyst,
  analystName,
  setAnalystName,
  analystDescription,
  setAnalystDescription,
  instructions,
  setInstructions,
  useDefaultScope,
  setUseDefaultScope,
  selectedMetricsWithEdges,
  setSelectedMetricsWithEdges,
  selectedBenchmarks,
  setSelectedBenchmarks,
  columnScopes,
  setColumnScopes,
  isCreatingNew = false,
  isLoadingDataScope = false,
}) => {

  // User context
  const { defaultTimeZone } = useUser();
  // Local state for UI only
  const [listOfMetrics, setListOfMetrics] = useState<Header[]>([]);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState<boolean>(false);
  const [isLoadingEdges, setIsLoadingEdges] = useState<Map<string, boolean>>(new Map());

  // Derived state from selectedMetricsWithEdges
  const selectedMetricHeaders = useMemo(
    () => selectedMetricsWithEdges.map(m => m.header).filter((h): h is Header => h !== undefined),
    [selectedMetricsWithEdges]
  );

  // Lists of available dependent headers (union from selected metrics)
  const [benchmarkList, setBenchmarkList] = useState<Header[]>([]);
  const [measureList, setMeasureList] = useState<Header[]>([]);
  const [timeframeList, setTimeframeList] = useState<Header[]>([]);
  const [attributeList, setAttributeList] = useState<Header[]>([]);

  // Derived selected items from columnScopes
  const selectedMeasures = useMemo(
    () => columnScopes
      .filter(f => f?.kg_column?.header?.column_type === ColumnType.MEASURE)
      .map(f => f?.kg_column?.header)
      .filter((h): h is Header => h !== undefined),
    [columnScopes]
  );

  const selectedTimeframes = useMemo(
    () => columnScopes
      .filter(f => f?.kg_column?.header?.column_type === ColumnType.TEMPORAL)
      .map(f => f?.kg_column?.header)
      .filter((h): h is Header => h !== undefined),
    [columnScopes]
  );

  const selectedAttributes = useMemo(
    () => columnScopes
      .filter(f => f?.kg_column?.header?.column_type === ColumnType.ATTRIBUTE)
      .map(f => f?.kg_column?.header)
      .filter((h): h is Header => h !== undefined),
    [columnScopes]
  );

  // Attribute filters only (for "Applied Filters" section)
  const attributeFilters = useMemo(
    () => columnScopes.filter(f => f?.kg_column?.header?.column_type === ColumnType.ATTRIBUTE),
    [columnScopes]
  );

  // Filter state for editing
  const [valuesList, setValuesList] = useState<string[]>([]);
  const [isLoadingValues, setIsLoadingValues] = useState<boolean>(false);
  const [showValuesSection, setShowValuesSection] = useState<boolean>(false);
  const [selectedFilterOperator, setSelectedFilterOperator] = useState<FilterOperator>(FilterOperator.EQ);
  const [selectedFilterValues, setSelectedFilterValues] = useState<string[]>([]);
  const [editingFilter, setEditingFilter] = useState<ColumnScopeProto | null>(null);
  const [editingFilterIndex, setEditingFilterIndex] = useState<number>(-1);

  // Modal state for adding/editing filters
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [isEditingExistingFilter, setIsEditingExistingFilter] = useState<boolean>(false);

  // Collapsible constraints section
  const [isConstraintsExpanded, setIsConstraintsExpanded] = useState<boolean>(true);

  // Reset state when creating new analyst
  useEffect(() => {
    if (isCreatingNew) {
      setInstructions("");
      setUseDefaultScope(true);
      setSelectedMetricsWithEdges([]);
      setSelectedBenchmarks([]);
      setColumnScopes([]);
    }
  }, [isCreatingNew]);

  // Fetch metrics list when useDefaultScope is false
  useEffect(() => {
    if (!useDefaultScope) {
      fetchMetricsHeaders();
    }
  }, [useDefaultScope]);

  const fetchMetricsHeaders = async () => {
    try {
      setIsLoadingMetrics(true);
      const metrics = await listMetrics("");
      setListOfMetrics(metrics || []);
    } catch (error) {
      showToast("Failed to fetch Metrics", "", "error");
    } finally {
      setIsLoadingMetrics(false);
    }
  };

  // Build union lists from selected metrics
  useEffect(() => {
    if (selectedMetricsWithEdges.length === 0) {
      // Clear everything when no metrics
      setBenchmarkList([]);
      setMeasureList([]);
      setTimeframeList([]);
      setAttributeList([]);
      setSelectedBenchmarks([]);
      setColumnScopes([]);
      return;
    }

    // Build union of all headers from all metrics
    const allHeadersMap = new Map<string, Header>();

    selectedMetricsWithEdges.forEach(metric => {
      metric.edges?.forEach((edge) => {
        if (edge.header && edge.header.id) {
          // Only include relevant edge types
          if (
            edge.edge_type === MetricEdgeType.METRIC_BENCHMARK_EDGE ||
            edge.edge_type === MetricEdgeType.METRIC_ATTRIBUTE_EDGE ||
            edge.edge_type === MetricEdgeType.METRIC_MEASURE_EDGE ||
            edge.edge_type === MetricEdgeType.METRIC_TIMEFRAME_EDGE
          ) {
            allHeadersMap.set(edge.header.id, edge.header);
          }
        }
      });
    });

    // Create union lists from all headers
    const allHeaders = Array.from(allHeadersMap.values());

    const newBenchmarkList = allHeaders.filter((header) => header.type === MetadataTypeEnum.BENCHMARK);
    const newMeasureList = allHeaders.filter((header) => header.column_type === ColumnType.MEASURE);
    const newTimeframeList = allHeaders.filter((header) => header.column_type === ColumnType.TEMPORAL);
    const newAttributeList = allHeaders.filter((header) => header.column_type === ColumnType.ATTRIBUTE);

    // Update all lists
    setBenchmarkList(newBenchmarkList);
    setMeasureList(newMeasureList);
    setTimeframeList(newTimeframeList);
    setAttributeList(newAttributeList);

    // Clean up selections that are no longer available
    setSelectedBenchmarks(prev =>
      prev.filter(h => newBenchmarkList.some(item => item.id === h.id))
    );

    setColumnScopes(prev =>
      prev.filter(filter => {
        const header = filter?.kg_column?.header;
        if (!header?.id) return true;
        if (header.column_type === ColumnType.MEASURE) return newMeasureList.some(m => m.id === header.id);
        if (header.column_type === ColumnType.TEMPORAL) return newTimeframeList.some(t => t.id === header.id);
        if (header.column_type === ColumnType.ATTRIBUTE) return newAttributeList.some(a => a.id === header.id);
        return true;
      })
    );
  }, [selectedMetricsWithEdges]);

  const isMetricSelected = (metricId?: string) =>
    !!metricId && selectedMetricHeaders.some((metric) => metric.id === metricId);

  const toggleMetricSelection = async (metric: Header) => {
    if (!metric.id) return;
    const isCurrentlySelected = selectedMetricHeaders.some((item) => item.id === metric.id);

    if (isCurrentlySelected) {
      // Remove metric
      setSelectedMetricsWithEdges(prev => prev.filter(m => m.header?.id !== metric.id));
    } else {
      // Add metric - fetch with edges
      await fetchMetricWithEdges(metric.id);
    }
  };

  const fetchMetricWithEdges = async (metricId: string) => {
    setIsLoadingEdges(prev => {
      const newMap = new Map(prev);
      newMap.set(metricId, true);
      return newMap;
    });

    try {
      const metric = await getMetricById(metricId, true);
      if (metric) {
        setSelectedMetricsWithEdges(prev => [...prev, metric]);
      } else {
        showToast("Failed to fetch Metric", "", "error");
      }
    } catch (error) {
      showToast("Failed to fetch Metric edges", "", "error");
    } finally {
      setIsLoadingEdges(prev => {
        const newMap = new Map(prev);
        newMap.set(metricId, false);
        return newMap;
      });
    }
  };

  const removeSelectedMetric = (metricId?: string) => {
    if (!metricId) return;
    setSelectedMetricsWithEdges(prev => prev.filter(m => m.header?.id !== metricId));
  };

  const isAnyEdgesLoading = useMemo(
    () => Array.from(isLoadingEdges.values()).some(Boolean),
    [isLoadingEdges]
  );

  const toggleBenchmarkSelection = (header: Header) => {
    if (!header.id) return;
    setSelectedBenchmarks(prev =>
      prev.some(item => item.id === header.id)
        ? prev.filter(item => item.id !== header.id)
        : [...prev, header]
    );
  };

  const toggleMeasureSelection = (header: Header) => {
    if (!header.id) return;
    const isSelected = selectedMeasures.some(m => m.id === header.id);

    if (isSelected) {
      // Remove from columnScopes
      setColumnScopes(prev => prev.filter(f => f?.kg_column?.header?.id !== header.id));
    } else {
      // Add to columnScopes
      setColumnScopes(prev => [...prev, ColumnScopeProto.create({
        kg_column: KGColumnProto.create({ header: header }),
      })]);
    }
  };

  const toggleTimeframeSelection = (header: Header) => {
    if (!header.id) return;
    const isSelected = selectedTimeframes.some(t => t.id === header.id);

    if (isSelected) {
      // Remove from columnScopes
      setColumnScopes(prev => prev.filter(f => f?.kg_column?.header?.id !== header.id));
    } else {
      // Add to columnScopes
      setColumnScopes(prev => [...prev, ColumnScopeProto.create({
        kg_column: KGColumnProto.create({ header: header }),
      })]);
    }
  };

  const toggleAttributeSelection = (header: Header) => {
    if (!header.id) return;
    const isSelected = selectedAttributes.some(a => a.id === header.id);

    if (isSelected) {
      // Remove all filters for this attribute from columnScopes
      setColumnScopes(prev => prev.filter(f => f?.kg_column?.header?.id !== header.id));
    } else {
      // Add to columnScopes without operator/values (entire column scope)
      setColumnScopes(prev => [...prev, ColumnScopeProto.create({
        kg_column: KGColumnProto.create({ header: header }),
      })]);
    }
  };

  // Remove filter means set operator and values to undefined and empty array
  const handleRemoveFilter = (filterId: string) => {
    setColumnScopes(prev =>
      prev.map(f =>
        f?.kg_column?.header?.id === filterId
          ? { ...f, operator: undefined, values: [] }
          : f
      )
    );
  };

  const handleFilterHidden = (filterId: string) => {
    setColumnScopes(prev =>
      prev.map(f =>
        f?.kg_column?.header?.id === filterId
          ? { ...f, is_hidden: !f.is_hidden }
          : f
      )
    );
  };

  const fetchValuesForEditingFilter = async (filter: ColumnScopeProto, filterIndex: number) => {
    if (!filter?.kg_column) return;

    setIsLoadingValues(true);
    setEditingFilter(filter);

    const filtersAbove = columnScopes.slice(0, filterIndex);
    const filtersBelow = columnScopes.slice(filterIndex + 1);

    const validFilters = filtersAbove.filter(f =>
      f.operator !== undefined &&
      f.values &&
      f.values.length > 0
    );

    const validFiltersBelow = filtersBelow.filter(f =>
      f.operator !== undefined &&
      f.values &&
      f.values.length > 0
    );

    try {
      const filtersForRequest = [...validFilters, ...validFiltersBelow].map(f => ({
        ...f,
        is_hidden: false
      }));

      const request = KGObservationProto.create({
        metrics: selectedMetricsWithEdges,
        columns: [ObservationColumnProto.create({
          column_id: filter.kg_column?.header?.id,
          kg_column_header: filter.kg_column?.header,
        })],
        filters: filtersForRequest.map(f => FilterProto.create({
          filter_id: f.kg_column?.header?.id,
          kg_column_header: f.kg_column?.header,
          operator: f.operator,
          values: f.values,
          is_hidden: f.is_hidden,
        })),
        calculate_non_measure_only: true,
      });

      const values = await getKGObservationData([request]);
      const valuesList = JSON.parse(values?.[0]?.kg_observation_data?.non_measure_values[0]?.data_value_json_array || "[]");
      setValuesList((valuesList || []).map(String));

      setEditingFilterIndex(filterIndex);
      setSelectedFilterOperator(filter.operator || FilterOperator.EQ);
      setSelectedFilterValues(filter.values || []);
      setShowValuesSection(true);
    } catch (error) {
      console.error("Error fetching attribute values for editing:", error);
      showToast("Failed to fetch values", "", "error");
      setValuesList([]);
    } finally {
      setIsLoadingValues(false);
    }
  };

  const handleSaveEditedFilter = () => {
    if (!editingFilter || editingFilterIndex === -1 || selectedFilterValues.length === 0) {
      showToast("Please select values", "You must select at least one value to save the filter", "warning");
      return;
    }

    setColumnScopes(prev => {
      const updated = [...prev];
      updated[editingFilterIndex] = ColumnScopeProto.create({
        kg_column: KGColumnProto.create({ header: editingFilter.kg_column?.header }),
        operator: selectedFilterOperator,
        values: selectedFilterValues,
      });
      return updated;
    });

    // Reset editing state
    setEditingFilter(null);
    setEditingFilterIndex(-1);
    setSelectedFilterValues([]);
    setSelectedFilterOperator(FilterOperator.EQ);
    setShowValuesSection(false);
    setValuesList([]);

    showToast("Filter updated", "Filter has been updated successfully", "success");
  };


  // Modal functions
  const openFilterModal = () => {
    setIsEditingExistingFilter(false);
    setIsFilterModalOpen(true);
  };

  const openFilterModalForEdit = (filter: ColumnScopeProto, index: number) => {
    setIsEditingExistingFilter(true);
    setIsFilterModalOpen(true);
    // Fetch values for the filter
    fetchValuesForEditingFilter(filter, index);
  };

  const closeFilterModal = () => {
    setIsFilterModalOpen(false);
    setIsEditingExistingFilter(false);
    // Reset editing state when closing modal
    setEditingFilter(null);
    setEditingFilterIndex(-1);
    setSelectedFilterValues([]);
    setSelectedFilterOperator(FilterOperator.EQ);
    setShowValuesSection(false);
    setValuesList([]);
  };

  const handleAttributeSelectionInModal = (filter: ColumnScopeProto, index: number) => {
    // Use existing fetchValuesForEditingFilter function
    fetchValuesForEditingFilter(filter, index);
  };

  const handleSaveFilterFromModal = () => {
    // Use existing handleSaveEditedFilter function and close modal
    handleSaveEditedFilter();
    closeFilterModal();
  };

  const renderSelectedTags = (
    headers: Header[],
    onRemove: (header: Header) => void,
    label: string
  ) => {
    if (headers.length === 0) return null;
    return (
      <Box>
        <Wrap spacing={2} mt={3}>
          {headers.map((header) => (
            <WrapItem key={header.id}>
              <Tag size="md" borderRadius="lg" variant="solid" py={1} bg="gray.50" borderWidth="1px" borderColor="gray.800">
                <TagLabel fontWeight="600" color="gray.800">{header?.name}</TagLabel>
                <TagCloseButton onClick={() => onRemove(header)} color="gray.800" />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      </Box>
    );
  };

  const getLabelIcon = (label: string) => {
    switch (label) {
      case "Attributes": return <Icon as={FaChartPie} boxSize={4} color="gray.800" minW={6} />;
      case "Timeframes": return <Icon as={FiCalendar} boxSize={5} color="gray.800" minW={6} />;
      case "Benchmarks": return <Icon as={PiTarget} boxSize={6} color="gray.800" minW={6} />;
      case "Measures": return <Icon as={MdNumbers} boxSize={6} color="gray.800" minW={6} />;
      default: return null;
    }
  };

  const renderDependentHeaderMenu = (
    label: string,
    headers: Header[],
    selected: Header[],
    onToggle: (header: Header) => void
  ) => {
    if (headers.length === 0) return null;

    const isSelected = (header: Header) => {
      if (!header?.id) return false;
      return selected.some((item) => item.id === header.id);
    };


    return (
      <FormControl>
        <HStack justify="space-between" align="center">
          <HStack spacing={2}>
            {getLabelIcon(label)}
            <Text fontSize="md" fontWeight="600" color="gray.800" textAlign="left" mb={0}>
              {label}
            </Text>
          </HStack>
          <HStack>
            <Menu closeOnSelect={false} autoSelect={false}>
              <MenuButton
                as={Button}
                size="sm"
                rightIcon={<ChevronDownIcon />}
                variant="outline"
                colorScheme="gray"
                isDisabled={headers.length === 0}
              >
                {selected.length}/{headers.length} selected
              </MenuButton>
              <Portal>
                <MenuList maxH="300px" overflowY="auto" minW="280px" px={1} zIndex="dropdown">
                  {headers.map((header) => (
                    <MenuItem
                      key={header.id}
                      py={2}
                      _hover={{ bg: "purple.50" }}
                      _focus={{ bg: "purple.50" }}
                      onClick={(event) => {
                        event.stopPropagation();
                        onToggle(header);
                      }}
                    >
                      <HStack spacing={3} align="center" w="100%">
                        <Checkbox
                          isChecked={isSelected(header)}
                          colorScheme="purple"
                          pointerEvents="none"
                          flexShrink={0}
                          m={0}
                        />
                        <Text fontSize="sm" fontWeight="500" color="gray.700" m={0}>
                          {header.name}
                        </Text>
                      </HStack>
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
          </HStack>
        </HStack>
        {renderSelectedTags(selected, (header) => onToggle(header), label)}
        {label === "Attributes" && !isCreatingNew && (
          <HStack spacing={3} mt={2} align="center" justify="space-between">
            <Text fontSize="sm" color="gray.500" mb={0}>
              Last synced:{" "}
              <Text as="span" fontWeight="600" color="gray.600">
                {originalAnalyst?.data_scope?.last_refresh_time_ms
                  ? `${epochConverterWithTimeZone(originalAnalyst.data_scope.last_refresh_time_ms, "dd-MMM-YYYY, HH:mm", defaultTimeZone)} ${defaultTimeZone}`
                  : "Never"}
              </Text>
            </Text>
            <Button
              colorScheme="gray"
              variant="solid"
              size="sm"
              onClick={() => {
                refreshAnalystDataScope(originalAnalyst?.header?.id || "", true);
              }}
              leftIcon={<Icon as={FaSync} boxSize={3} />}
              fontSize="sm"
              fontWeight="600"
              borderRadius="lg"
            >
              Sync Attribute Values
            </Button>
          </HStack>
        )}
      </FormControl>
    );
  };

  return (
    <Box
      w={useDefaultScope ? "70%" : "100%"}
      mx="auto"
      h="100%"
      overflowY="auto"
      px={useDefaultScope ? 0 : 0}
      pb={0}
      pt={useDefaultScope ? 8 : 2}
      sx={{
        "&::-webkit-scrollbar": {
          width: "6px"
        },
        "&::-webkit-scrollbar-track": {
          bg: "transparent"
        },
        "&::-webkit-scrollbar-thumb": {
          bg: "gray.300",
          borderRadius: "full"
        },
        "&::-webkit-scrollbar-thumb:hover": {
          bg: "gray.400"
        },
        scrollbarWidth: "thin",
        scrollbarColor: "var(--chakra-colors-gray-300) transparent"
      }}
    >
      <Grid
        templateColumns="repeat(2, 1fr)"
        gap={3}
        w="100%"
        minW="600px"
        templateRows="auto"
        alignItems="stretch"
      >
        {/* Box 1: Identity + Data usage guidelines + Scope toggle */}
        <GridItem colSpan={useDefaultScope ? 2 : 1}>
          <Box
            bg="white"
            borderRadius="lg"
            border="1px"
            borderColor="gray.200"
            p={6}
            boxShadow="sm"
            display="flex"
            flexDirection="column"
            h="100%"
          >
            <VStack
              align="stretch"
              spacing={2}
            >
              <HStack spacing={3} mb={1}>

                <Icon as={FaUser} boxSize={5} color="purple.700" />
                <Text fontSize="md" fontWeight="700" color="purple.700" mb={0} textAlign="left">
                  Identity
                </Text>

              </HStack>
              <Divider my={0} borderColor="purple.600" />
              <FormControl>
                <FormLabel fontSize="sm" mb={2} fontWeight="600" color="gray.700">
                  Name
                </FormLabel>
                <Input
                  size="sm"
                  value={analystName}
                  onChange={(e) => setAnalystName(e.target.value)}
                  placeholder="Enter agent name"
                  _focus={{
                    borderColor: "purple.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)"
                  }}
                  fontWeight="500"
                  _hover={{ bg: "white" }}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" mb={2} fontWeight="600" color="gray.700">
                  Description
                </FormLabel>
                <Input
                  size="sm"
                  value={analystDescription}
                  onChange={(e) => setAnalystDescription(e.target.value)}
                  placeholder="Enter agent description"
                  _focus={{
                    borderColor: "purple.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)"
                  }}
                  fontWeight="500"
                  _hover={{ bg: "white" }}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" mb={2} fontWeight="600" color="gray.700">
                  Instructions
                </FormLabel>
                <Textarea
                  size="sm"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Enter guidelines or instructions for using this data scope..."
                  rows={7}
                  _focus={{
                    borderColor: "purple.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)"
                  }}
                  fontWeight="500"
                  _hover={{ bg: "white" }}
                  resize="none"
                />
              </FormControl>
              <HStack spacing={3} mt={3} mb={1}>

                <Icon as={FaDatabase} boxSize={5} color="purple.700" />
                <Text fontSize="md" fontWeight="700" color="purple.700" mb={0} textAlign="left">
                  Data Access
                </Text>

              </HStack>
              <Divider my={0} borderColor="purple.600" />

              {/* Use Default Scope Checkbox */}

              <FormControl>
                <HStack spacing={3} align="center" justify={useDefaultScope ? "space-between" : "center"} mt={5}>
                  {useDefaultScope && (
                    <Box>


                      <HStack spacing={2} align="center" as="span">
                        <Icon as={CheckIcon} boxSize={5} color="green.500" />
                        <Text
                          fontSize="lg"
                          fontWeight="600"
                          color={useDefaultScope ? "gray.800" : "gray.400"}
                          mb={0}
                        >
                          All metrics and related entities
                        </Text>
                      </HStack>
                      <Text fontSize="sm" color="gray.500" mt={1} mb={0}>
                        Grant analyst access to all metrics, benchmarks, and attributes
                      </Text>
                    </Box>
                  )}

                  <Button
                    size="lg"
                    variant={useDefaultScope ? "solid" : "outline"}
                    colorScheme="purple"
                    color={useDefaultScope ? "white" : "gray.800"}
                    bg={useDefaultScope ? "purple.700" : "white"}
                    _hover={{ bg: useDefaultScope ? "purple.600" : "gray.100" }}
                    onClick={() => setUseDefaultScope(!useDefaultScope)}
                    leftIcon={useDefaultScope ? <Icon as={IoMdContract} boxSize={7} /> : <Icon as={FaExpandAlt} boxSize={5} />}
                  >
                    <Text fontSize="md" fontWeight={useDefaultScope ? "700" : "500"} mb={0}>
                      {useDefaultScope ? "Refine Data Access" : "Grant access to all metrics and related entities"}
                    </Text>
                  </Button>

                </HStack>
              </FormControl>
              {/* Warning when using default scope */}
              {useDefaultScope && (
                <HStack
                  spacing={3}
                  bg="yellow.50"
                  border="1px"
                  borderColor="yellow.200"
                  borderRadius="md"
                  p={3}
                  mt={2}
                >
                  <WarningIcon color="yellow.600" boxSize={6} />
                  <Text fontSize="md" color="yellow.800" fontWeight="600" mb={0} textAlign="left">
                    Giving the analyst access to everything can introduce noise and errors. Please refine data access to what this analyst actually needs.
                  </Text>
                </HStack>
              )}
            </VStack>
          </Box>
        </GridItem>

        {/* Box 2: Refine Data Access - Metric Selection + Scope Related Entities - only when custom scope */}
        {!useDefaultScope && (
          <GridItem>
            <Box
              bg="white"
              borderRadius="lg"
              border="1px"
              borderColor="gray.200"
              p={6}
              boxShadow="sm"
              display="flex"
              flexDirection="column"
              h="100%"
            >
              <VStack align="stretch" spacing={2}>
                {/* Refine Data Access heading */}
                <HStack spacing={2} mb={1}>
                  <Icon as={IoMdContract} boxSize={6} color="purple.700" />
                  <Text fontSize="md" fontWeight="700" color="purple.700" mb={0} textAlign="left">
                    Refine Data Access
                  </Text>
                </HStack>
                <Divider my={0} borderColor="purple.600" />

                <VStack align="stretch" spacing={2}>
                  {/* Select metrics section */}
                  <Box>
                    <HStack justify="space-between" align="center" mb={2}>
                      <HStack spacing={2}>
                        <Icon as={MdBarChart} boxSize={5} color="gray.800" />
                        <Text fontSize="md" fontWeight="600" color="gray.800" mb={0} textAlign="left">
                          Metrics
                        </Text>
                      </HStack>

                      <Menu closeOnSelect={false} autoSelect={false}>
                        <MenuButton
                          as={Button}
                          size="sm"
                          rightIcon={<ChevronDownIcon />}
                          colorScheme="gray"
                          variant="outline"
                          isLoading={isLoadingMetrics}
                          isDisabled={isLoadingMetrics || listOfMetrics.length === 0}
                        >
                          {selectedMetricHeaders.length > 0
                            ? `${selectedMetricHeaders.length} selected`
                            : "Select metrics"}
                        </MenuButton>
                        <Portal>
                          <MenuList maxH="300px" overflowY="auto" minW="280px" px={1} zIndex="dropdown">
                            {isLoadingMetrics ? (
                              <VStack py={4}>
                                <Spinner size="sm" color="purple.500" />
                                <Text fontSize="sm" color="gray.500">
                                  Loading metrics...
                                </Text>
                              </VStack>
                            ) : listOfMetrics.length > 0 ? (
                              listOfMetrics.map((metric) => (
                                <MenuItem
                                  key={metric.id}
                                  display="flex"
                                  alignItems="flex-start"
                                  gap={3}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    toggleMetricSelection(metric);
                                  }}
                                  closeOnSelect={false}
                                  _hover={{ bg: "purple.50" }}
                                  _focus={{ bg: "purple.50" }}
                                >
                                  <Checkbox
                                    isChecked={isMetricSelected(metric.id)}
                                    colorScheme="purple"
                                    pointerEvents="none"
                                    mt={1}
                                    flexShrink={0}
                                    mr={1}
                                  />
                                  <Text fontSize="sm" fontWeight="600" color="gray.700" mb={0}>
                                    {metric.name}
                                  </Text>
                                </MenuItem>
                              ))
                            ) : (
                              <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                                No metrics available
                              </Text>
                            )}
                          </MenuList>
                        </Portal>
                      </Menu>
                    </HStack>

                    {/* Selected Metrics Tags */}
                    {isLoadingDataScope ? (
                      <HStack spacing={2} mt={2} px={1}>
                        <Spinner size="sm" color="purple.500" thickness="2px" />
                        <Text fontSize="sm" color="gray.500" mb={0}>Loading selected metrics...</Text>
                      </HStack>
                    ) : selectedMetricHeaders.length > 0 && (
                      <Wrap spacing={2} mt={2}>
                        {selectedMetricHeaders.map((metric) => (
                          <WrapItem key={metric.id}>
                            <Tag
                              size="md"
                              borderRadius="md"
                              colorScheme="purple"
                              variant="solid"
                              px={3}
                              py={1}
                              bg="gray.50"
                              borderWidth="1px"
                              borderColor="gray.800"
                            >
                              <TagLabel fontWeight="600" color="gray.800">{metric.name}</TagLabel>
                              <TagCloseButton onClick={() => removeSelectedMetric(metric.id)} color="gray.800" />
                            </Tag>
                          </WrapItem>
                        ))}
                      </Wrap>
                    )}
                  </Box>

                  {/* Scope related entities section */}
                  {selectedMetricHeaders.length > 0 && (
                    <Box>
                      <VStack spacing={5} align="stretch" alignItems="flex-start">
                        {benchmarkList.length === 0 &&
                          measureList.length === 0 &&
                          timeframeList.length === 0 &&
                          attributeList.length === 0 &&
                          (isAnyEdgesLoading ? (
                            <HStack spacing={2} justify="center" py={4}>
                              <Spinner size="sm" color="purple.500" />
                              <Text fontSize="sm" color="gray.500">
                                Loading available data components...
                              </Text>
                            </HStack>
                          ) : (
                            <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                              No data components available for the selected metrics
                            </Text>
                          ))}
                        {renderDependentHeaderMenu(
                          "Attributes",
                          attributeList,
                          selectedAttributes,
                          toggleAttributeSelection
                        )}
                        {/* Constrain Attribute Values - collapsible, visually nested inside Attributes */}
                        {attributeList.length > 0 && (() => {
                          const activeConstraints = attributeFilters.filter(f => f.operator !== undefined && f.values && f.values.length > 0);
                          return (
                            <Box
                              bg="white"
                              borderRadius="md"
                              border="2px solid"
                              borderColor="purple.600"
                              px={2}
                              pt={1.5}
                              pb={1.5}
                              ml={0}
                              mt={-2}
                              mr={2}
                              w="100%"
                            >
                              {/* Collapsible header */}
                              <HStack
                                justify="space-between"
                                align="center"
                                cursor="pointer"
                                onClick={() => setIsConstraintsExpanded(v => !v)}
                                userSelect="none"
                              >
                                <HStack spacing={2}>
                                  <Text fontSize="xs" fontWeight="700" color="purple.700" mb={0}>
                                    Constrain Attribute Values
                                  </Text>
                                  {activeConstraints.length > 0 && (
                                    <Tag size="sm" colorScheme="purple" bg="purple.600" color="white" borderRadius="full" fontSize="xs" fontWeight="700" px={2}>
                                      {activeConstraints.length}
                                    </Tag>
                                  )}
                                  <Tooltip
                                    label={getTooltip("data-scope-configurator.attribute_values.tooltip") || ""}
                                    placement="top"
                                    hasArrow
                                    bg="gray.700"
                                    color="white"
                                    fontSize="sm"
                                    px={3}
                                    py={2}
                                    borderRadius="md"
                                  >
                                    <Box as="span" cursor="help" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                                      <Icon as={IoMdInformationCircleOutline} boxSize={4} color="purple.400" />
                                    </Box>
                                  </Tooltip>
                                </HStack>
                                <HStack spacing={1}>
                                  <Button
                                    leftIcon={<AddIcon boxSize={3} />}
                                    size="2xs"
                                    variant="solid"
                                    colorScheme="purple"
                                    bg="purple.700"
                                    onClick={e => { e.stopPropagation(); openFilterModal(); }}
                                    fontSize="xs"
                                    _hover={{ bg: "purple.600" }}
                                  >
                                    Add
                                  </Button>
                                  <Icon
                                    as={isConstraintsExpanded ? ChevronUpIcon : ChevronDownIcon}
                                    boxSize={4}
                                    color="purple.400"
                                  />
                                </HStack>
                              </HStack>

                              {/* Collapsible filter list */}
                              <Collapse in={isConstraintsExpanded} animateOpacity>
                                <VStack spacing={0} align="stretch" mt={activeConstraints.length > 0 ? 1 : 0}>
                                  {activeConstraints.map((filter) => {
                                    const actualIndex = columnScopes.findIndex(f => f?.kg_column?.header?.id === filter?.kg_column?.header?.id);
                                    return (
                                      <React.Fragment key={filter?.kg_column?.header?.id}>
                                        <Flex gap={2} alignItems="center" justifyContent="space-between" py={0}>
                                          <Flex
                                            gap={1}
                                            alignItems="center"
                                            flex="1"
                                            flexWrap="wrap"
                                            opacity={filter.is_hidden ? 0.5 : 1}
                                            transition="opacity 0.15s"
                                            position="relative"
                                            _before={filter.is_hidden ? {
                                              content: "''",
                                              position: "absolute",
                                              top: 0,
                                              left: 0,
                                              right: 0,
                                              bottom: 0,
                                              background: "repeating-linear-gradient(45deg, transparent, transparent 6px, rgba(128, 90, 213, 0.12) 6px, rgba(128, 90, 213, 0.12) 12px)",
                                              pointerEvents: "none",
                                              borderRadius: "md",
                                              zIndex: 1,
                                            } : {}}
                                          >
                                            <Tag
                                              size="sm"
                                              bg="white"
                                              fontWeight="600"
                                              flexShrink={0}
                                              borderRadius="lg"
                                              borderWidth="1px"
                                              borderColor="gray.300"
                                              px={2}
                                              py={1}
                                              color="gray.700"
                                            >
                                              {filter?.kg_column?.header?.name}
                                            </Tag>
                                            <Text fontSize="xs" fontWeight="700" color="gray.500" mb={0} flexShrink={0}>
                                              {filter.operator === FilterOperator.EQ ? "=" : "≠"}
                                            </Text>
                                            <Flex gap={1} flexWrap="wrap">
                                              {filter?.values?.slice(0, 4).map((value, idx) => (
                                                <Tag
                                                  key={idx}
                                                  size="sm"
                                                  px={2}
                                                  py={1}
                                                  fontWeight="600"
                                                  variant="outline"
                                                  colorScheme="purple"
                                                  whiteSpace="nowrap"
                                                  bg="white"
                                                  color="gray.700"
                                                  borderRadius="lg"
                                                  borderColor="purple.200"
                                                >
                                                  {value}
                                                </Tag>
                                              ))}
                                              {filter?.values && filter.values.length > 4 && (
                                                <Tag size="sm" variant="outline" colorScheme="purple" fontWeight="600" whiteSpace="nowrap">
                                                  +{filter.values.length - 4} more
                                                </Tag>
                                              )}
                                            </Flex>
                                          </Flex>

                                          {/* Kebab menu */}
                                          {filter?.kg_column?.header?.id && (
                                            <Menu placement="bottom-end" autoSelect={false}>
                                              <MenuButton
                                                as={IconButton}
                                                aria-label="Filter options"
                                                icon={<Icon as={BsThreeDotsVertical} boxSize={3.5} />}
                                                size="xs"
                                                variant="ghost"
                                                colorScheme="purple"
                                                flexShrink={0}
                                                onClick={e => e.stopPropagation()}
                                              />
                                              <Portal>
                                                <MenuList minW="180px" fontSize="sm" zIndex="dropdown">
                                                  {filter?.operator && filter?.values && filter.values.length > 0 && (
                                                    <>
                                                      <MenuItem
                                                        closeOnSelect={false}
                                                        onClick={e => { e.stopPropagation(); handleFilterHidden(filter?.kg_column?.header?.id || ""); }}
                                                      >
                                                        <HStack justify="space-between" w="100%">
                                                          <Text mb={0} fontSize="sm" fontWeight="500">{filter.is_hidden ? "Hidden" : "Visible"}</Text>
                                                          <Switch
                                                            size="sm"
                                                            colorScheme="purple"
                                                            isChecked={!filter.is_hidden}
                                                            onChange={() => handleFilterHidden(filter?.kg_column?.header?.id || "")}
                                                            onClick={e => e.stopPropagation()}
                                                            mb={0}
                                                          />
                                                        </HStack>
                                                      </MenuItem>
                                                      <MenuDivider />
                                                    </>
                                                  )}
                                                  <MenuItem
                                                    icon={<EditIcon />}
                                                    onClick={e => { e.stopPropagation(); openFilterModalForEdit(filter, actualIndex); }}
                                                  >
                                                    Edit values
                                                  </MenuItem>
                                                  <MenuItem
                                                    icon={<DeleteIcon color="red.400" />}
                                                    color="red.500"
                                                    onClick={e => { e.stopPropagation(); handleRemoveFilter(filter?.kg_column?.header?.id || ""); }}
                                                  >
                                                    Delete
                                                  </MenuItem>
                                                </MenuList>
                                              </Portal>
                                            </Menu>
                                          )}
                                        </Flex>
                                      </React.Fragment>
                                    );
                                  })}
                                </VStack>
                              </Collapse>
                            </Box>
                          );
                        })()}
                        {renderDependentHeaderMenu(
                          "Timeframes",
                          timeframeList,
                          selectedTimeframes,
                          toggleTimeframeSelection
                        )}
                        {renderDependentHeaderMenu(
                          "Benchmarks",
                          benchmarkList,
                          selectedBenchmarks,
                          toggleBenchmarkSelection
                        )}
                        {renderDependentHeaderMenu(
                          "Measures",
                          measureList,
                          selectedMeasures,
                          toggleMeasureSelection
                        )}
                      </VStack>
                    </Box>
                  )}
                </VStack>
              </VStack>
            </Box>
          </GridItem>
        )}

      </Grid>

      {/* Filter Modal for Adding/Editing Filters */}
      <Modal isOpen={isFilterModalOpen} onClose={closeFilterModal} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="650px">
          <ModalHeader>{isEditingExistingFilter ? "Edit Filter" : "Add Filter"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={5} align="stretch">
              {/* Attribute Selection Menu - Disabled when editing existing filter */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                  Select Attribute
                </FormLabel>
                <Menu>
                  <MenuButton
                    as={Button}
                    rightIcon={isEditingExistingFilter ? undefined : <ChevronDownIcon />}
                    w="100%"
                    textAlign="left"
                    variant="outline"
                    fontWeight="500"
                    isDisabled={isLoadingValues || isEditingExistingFilter}
                    cursor={isEditingExistingFilter ? "default" : "pointer"}
                  >
                    {editingFilter?.kg_column?.header?.name || "Choose an attribute"}
                  </MenuButton>
                  {!isEditingExistingFilter && (
                    <Portal>
                      <MenuList maxH="250px" overflowY="auto" minW="280px" zIndex={1500}>
                        {selectedAttributes.map((attr) => {
                          const filterIndex = columnScopes.findIndex(f => f?.kg_column?.header?.id === attr.id);
                          const filter = columnScopes[filterIndex];

                          return (
                            <MenuItem
                              key={attr.id}
                              onClick={() => filter && handleAttributeSelectionInModal(filter, filterIndex)}
                              _hover={{ bg: "purple.50" }}
                              _focus={{ bg: "purple.50" }}
                            >
                              <Text fontSize="sm" fontWeight="500" color="gray.700">
                                {attr.name}
                              </Text>
                            </MenuItem>
                          );
                        })}
                      </MenuList>
                    </Portal>
                  )}
                </Menu>
              </FormControl>

              {/* Loading state when fetching values */}
              {isLoadingValues && (
                <Flex justify="center" align="center" py={6}>
                  <Spinner size="md" mr={3} color="purple.500" />
                  <Text fontSize="md" color="gray.600">Loading values...</Text>
                </Flex>
              )}

              {/* Value Selection - Show when attribute is selected and values loaded */}
              {editingFilter && showValuesSection && !isLoadingValues && (
                <VStack spacing={4} align="stretch">
                  <HStack spacing={4} align="flex-start">
                    {/* Operator Selection */}
                    <FormControl maxW="160px">
                      <FormLabel fontSize="sm" fontWeight="600" mb={2} color="gray.700">
                        Operator
                      </FormLabel>
                      <Select
                        size="md"
                        value={selectedFilterOperator}
                        onChange={(e) => setSelectedFilterOperator(Number(e.target.value) as FilterOperator)}
                        fontWeight="500"
                        bg="white"
                      >
                        <option value={FilterOperator.EQ}>Equals (=)</option>
                        <option value={FilterOperator.NE}>Not Equals (≠)</option>
                      </Select>
                    </FormControl>

                    {/* Values Multi-Select */}
                    <FormControl flex="1">
                      <FormLabel fontSize="sm" fontWeight="600" mb={2} color="gray.700">
                        Select Values
                      </FormLabel>
                      <ColumnValues
                        contextValues={valuesList}
                        selectedOptions={selectedFilterValues}
                        setSelectedOptions={setSelectedFilterValues}
                        errorMsg=""
                      />
                    </FormControl>
                  </HStack>

                  {/* Action Buttons */}
                  <HStack justify="flex-end" spacing={3} pt={2}>
                    <Button variant="ghost" onClick={closeFilterModal}>
                      Cancel
                    </Button>
                    <Button
                      colorScheme="purple"
                      onClick={handleSaveFilterFromModal}
                      isDisabled={selectedFilterValues.length === 0}
                    >
                      {isEditingExistingFilter ? "Save" : "Add"}
                    </Button>
                  </HStack>
                </VStack>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DataScopeConfigurator;